import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "compact";
}

export default function ArticleCard({
  article,
  variant = "default",
}: ArticleCardProps) {
  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="flex flex-col sm:flex-row gap-4 group"
      >
        <div className="w-full sm:w-48 md:w-56 h-48 sm:h-32 md:h-36 shrink-0 rounded-xl overflow-hidden bg-background-100">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
              {article.category}
            </span>
            {article.hotTag && (
              <span className="px-2 py-0.5 rounded bg-primary-500 text-background-50 text-xs font-bold">
                {article.hotTag}
              </span>
            )}
          </div>
          <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
            {article.summary}
          </p>
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center" />
              {article.readCount > 10000
                ? `${(article.readCount / 10000).toFixed(1)}万`
                : article.readCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link to={`/article/${article.id}`} className="flex gap-3 group">
        <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-background-100">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex-1 flex flex-col justify-center">
          <h3 className="text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-1.5">
            {article.title}
          </h3>
          <span className="text-xs text-foreground-400">{article.publishDate}</span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-300"
    >
      <div className="aspect-[16/10] overflow-hidden bg-background-100">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2.5">
          <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
            {article.category}
          </span>
          {article.hotTag && (
            <span className="px-2 py-0.5 rounded bg-primary-500 text-background-50 text-xs font-bold">
              {article.hotTag}
            </span>
          )}
        </div>
        <h3 className="text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-500 transition-colors leading-snug mb-2 line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
          {article.summary}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span>{article.author}</span>
            <span>{article.publishDate}</span>
          </div>
          <span className="flex items-center gap-1 text-xs text-foreground-400">
            <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center" />
            {article.readCount > 10000
              ? `${(article.readCount / 10000).toFixed(1)}万`
              : article.readCount}
          </span>
        </div>
      </div>
    </Link>
  );
}